export const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

export const createPropertiesGroup = (type, attribute, group) => {
    const nameParts = attribute.split('-');
    return group.reduce((acc, item) => {
        const newParts = [...nameParts, item];
        const key = newParts.map((part, i) => (i ? capitalize(part) : part)).join('');
        acc[key] = { type, attribute: newParts.join('-') };
        return acc;
    }, {});
};

export const deferred = (callback, timeout) => {
    let toFunc;
    return (...args) => {
        if (toFunc) {
            clearTimeout(toFunc);
        }
        toFunc = setTimeout(callback, timeout, ...args);
    };
};

export const deferredWithPromise = (callback, timeout) => {
    let toFunc;
    return () =>
        new Promise((resolve, reject) => {
            if (toFunc) {
                clearTimeout(toFunc);
            }
            toFunc = setTimeout(() => {
                try {
                    callback();
                    resolve();
                } catch (e) {
                    reject(e);
                }
            }, timeout);
        });
};

export const jsLazyLoad = (url = '', parent = {}, key) =>
    new Promise((resolve, reject) => {
        if (parent[key]) {
            resolve(parent[key]);
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => resolve(parent[key]);
        script.onerror = reject;
        document.head.append(script);
    });

export const prepareHtml = (html = '') =>
    html
        .replace(/&quot;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');

export const getYoutubeVideoIcon = videoId => `https://img.youtube.com/vi/${videoId}/0.jpg`;

export const getVimeoVideoIcon = videoId =>
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
        .then(response => response.json())
        .then(data => data.thumbnail_url)
        .catch(() => {});

/**
 * Freeze the property of the object so it can't be changed anymore
 *
 * @param {object} obj - target object
 * @param {string} prop - the name of the property
 * @param {any} value - the value of the property (if not specified the current object property value will be used)
 *
 * Usage Example:
 *
 * This example will freeze 'readOnly' with current value on afterConnect
 * afterConnect() {
 *   freezeProperty(this, 'readOnly');
 * }
 */

export const freezeProperty = (obj, prop, value) => {
    Object.defineProperty(obj, prop, {
        value: value || obj[prop],
        writable: false,
        enumerable: true,
        configurable: false,
    });
};

export function disableDragging(nodes) {
    // IE/FF/Safari fix for drag events conflicts
    if (nodes && nodes.length) {
        [...nodes].forEach(node => {
            node.setAttribute('draggable', false);
            if (isVrootChild(node)) return;
            disableDragging(node.children);
        });
    }
}


export const setGroupDefaultValues = (ctx, sizesGroup, groupName, defaultValue) => {
    sizesGroup.forEach(size => {
        ctx[groupName + capitalize(size)] = defaultValue;
    });
};

export const stringToClassMapToken = classes => {
    const obj = {};

    classes &&
        classes.split(' ').map(el => {
            obj[el] = true;
        });

    return obj;
};

export const addListener = (element, event, callback, capturing = false) => {
    if (!element) {
        return null;
    }

    element.addEventListener(event, callback, capturing);
    return () => element.removeEventListener(event, callback, capturing);
};

export function callEventOnce(el, event, cb, capturing) {
    const newCb = () => {
        cb();
        el.removeEventListener(event, newCb, capturing);
    };
    el.addEventListener(event, newCb, capturing);
}

export function compareState(ctx, changedProps, name, subName = '', compareFunc) {
    if (changedProps && changedProps.has(name)) {
        let value = ctx[name];
        if (subName && value) {
            value = value[subName];
        }

        const nameToCompare = `__old${name}${subName}`;
        if (compareFunc) {
            value = compareFunc();
        }
        if (ctx[nameToCompare] !== value) {
            ctx[nameToCompare] = value;
            return true;
        }
    }
}

export const isVrootChild = el => el.firstElementChild && el.firstElementChild.tagName === 'V-ROOT';

export const vSlotDynamicNameRender = (name, showSlot) => {
    const vSlotString = `<v-slot name="${name}"></v-slot>`;
    const vSlotHtml = document.createElement('div');
    vSlotHtml.classList.add(showSlot ? 'd-contents' : 'd-none');
    vSlotHtml.innerHTML = vSlotString;
    return vSlotHtml;
};

export function cacheSelector(ctx, getSelectors, storeName) {
    const keyStore = storeName || ctx.storeName;
    const key = `__selectors__${keyStore}`;
    if (!ctx[key]) {
        ctx[key] = getSelectors(keyStore);
    }
    return ctx[key];
}

/**
 * Render the content of the <template> tag inside the webcomponent into the DOM
 *
 * @param {object} obj - target object
 *
 * Usage Example:
 *
 * This example will render 'HTML content' on firstUpdated of e2-example element
 * <e2-example>
 *   <template>
 *     HTML content
 *   </template>
 * </e2-example>
 *
 * firstUpdated() {
 *   renderTemplate(this);
 * }
 */

export function renderTemplate(ctx) {
    const template = ctx.querySelector('template');
    if (template) {
        const content = template.content.cloneNode(true);
        template.replaceWith(content);
    }
}
